import { useEffect, useState } from 'react'

import { getSignupMode, type SignupMode } from '@/platform/auth/services/waitlist.service'

// One read per page load, shared by every caller (login, register, landing, modal).
let cached: SignupMode | null = null
let pending: Promise<SignupMode> | null = null
const listeners = new Set<(m: SignupMode) => void>()

function load(): Promise<SignupMode> {
  pending ??= getSignupMode()
    .then((r) => r.mode)
    .catch(() => 'waitlist' as const)
    .then((m) => {
      cached = m
      return m
    })
  return pending
}

/** After a super-admin switches the mode: every mounted caller follows. */
export function setKnownSignupMode(mode: SignupMode): void {
  cached = mode
  pending = Promise.resolve(mode)
  listeners.forEach((l) => l(mode))
}

/**
 * The platform's sign-up mode (config `auth/signups`). While it is unknown —
 * loading, or the request failed — the closed default (`waitlist`) applies:
 * a sign-up form is never offered by mistake.
 */
export function useSignupMode(): { mode: SignupMode; isLoading: boolean } {
  const [mode, setMode] = useState<SignupMode | null>(cached)
  useEffect(() => {
    let alive = true
    listeners.add(setMode)
    if (cached === null) void load().then((m) => alive && setMode(m))
    return () => {
      alive = false
      listeners.delete(setMode)
    }
  }, [])
  return { mode: mode ?? 'waitlist', isLoading: mode === null }
}
